/**
 * Format enhanced symbol for display based on contract descriptions
 * 
 * @param {Object} trade - Trade object with symbol and contract description fields
 * @param {string} trade.symbol - Basic symbol (e.g., "AAPL")
 * @param {string} trade.sec_type - Security type ("STK", "OPT", "FUT", "CASH")
 * @param {string} trade.contract_description_1 - Basic contract description
 * @param {string} trade.contract_description_2 - Detailed contract description (options details)
 * @returns {string} Formatted symbol for display
 */
export const formatEnhancedSymbol = (trade) => {
  if (!trade) return '';
  
  const { symbol, sec_type, contract_description_1, contract_description_2 } = trade;
  
  // For options, show full contract details if available
  if (sec_type === 'OPT' && contract_description_2) {
    return `${symbol} ${contract_description_2}`;
  }
  
  // For forex, just show the symbol (e.g., "USD/CAD" instead of "USD/CAD (USD.CAD)")
  if (sec_type === 'CASH') {
    return symbol || '';
  }
  
  // For futures, just show the symbol (e.g., "ES DEC25" instead of "ES DEC25 (Dec19'25(50))")
  if (sec_type === 'FUT') {
    return symbol || '';
  }
  
  // For stocks and other securities with different description, show it in parentheses
  if (contract_description_1 && contract_description_1 !== symbol) {
    return `${symbol} (${contract_description_1})`;
  }
  
  // Fallback to basic symbol
  return symbol || '';
};

/**
 * Format options contract description for display
 * Handles various formats of options descriptions from broker
 * 
 * @param {string} contractDescription - Raw contract description from broker
 * @returns {string} Formatted options description
 */
export const formatOptionsDescription = (contractDescription) => {
  if (!contractDescription) return '';
  
  // Examples of contract descriptions:
  // "JUL 18 '25 212.5 Call" -> "Jul18'25 212.5C"
  // "DEC 20 '24 150.0 Put" -> "Dec20'24 150.0P"
  
  try {
    // Extract components with regex
    const match = contractDescription.match(/(\w{3})\s+(\d{1,2})\s+'(\d{2})\s+([\d.]+)\s+(Call|Put)/i);
    
    if (match) {
      const [, month, day, year, strike, type] = match;
      const shortType = type.toLowerCase() === 'call' ? 'C' : 'P';
      return `${month}${day}'${year} ${strike}${shortType}`;
    }
    
    // If regex doesn't match, return as-is
    return contractDescription;
  } catch (error) {
    console.warn('Error formatting options description:', error);
    return contractDescription;
  }
};

/**
 * Get display symbol for positions table
 * Shows enhanced symbol for options, basic symbol for others
 * 
 * @param {Object} trade - Trade object
 * @returns {string} Symbol for display in positions table
 */
export const getPositionDisplaySymbol = (trade) => {
  if (!trade) return '';
  
  const { symbol, sec_type, contract_description_2 } = trade;
  
  // For options, format more compactly for table display
  if (sec_type === 'OPT' && contract_description_2) {
    const formatted = formatOptionsDescription(contract_description_2);
    return `${symbol} ${formatted}`;
  }
  
  // For forex and futures, just show the basic symbol
  if (sec_type === 'CASH' || sec_type === 'FUT') {
    return symbol || '';
  }
  
  // For everything else, use the enhanced formatter
  return formatEnhancedSymbol(trade);
}; 